

import { BbpsSDK } from "softmint-bbps-sdk";
import "softmint-bbps-sdk/style.css";
import { useSession } from "hooks/useSession";
import React, { useState, useRef } from "react";
import { fetchWrapper } from "utils/apiFetch";

export default function SoftmintBBPS() {
  const { userid, token } = useSession();

  const sdkRef = useRef(null);
  const mountRef = useRef(null);

  const [launched, setLaunched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sdkError, setSdkError] = useState(null);
  const saveTransactionToBackend = async (sdkReceipt, statusOverride = null) => {
    try {
      const formData = new FormData();

      // Basic payload mapping
      formData.append("status", statusOverride || sdkReceipt?.status || "SUCCESS");
      formData.append("operatorName", sdkReceipt?.operatorName || sdkReceipt?.billerName || "");
      formData.append("cn", sdkReceipt?.cn || sdkReceipt?.consumerNumber || sdkReceipt?.accountNo || "");
      formData.append("amount", sdkReceipt?.amount || "0");
      formData.append("rrn", sdkReceipt?.rrn || "");
      formData.append("bankRefId", sdkReceipt?.bankRefId || "");
      formData.append("partnerRefId", sdkReceipt?.partnerRefId || sdkReceipt?.txnid || "");
      formData.append("timestamp", sdkReceipt?.timestamp || new Date().toISOString());
      formData.append("remark", sdkReceipt?.remark || sdkReceipt?.note || "");
      formData.append("id", sdkReceipt?.id || sdkReceipt?.billerId || "");

      // Error fields handling
      formData.append("message", sdkReceipt?.message || sdkReceipt?.errorMsg || "");
      formData.append("errStatus", sdkReceipt?.errStatus || (statusOverride === "FAILED" ? "FAILED" : "PENDING"));

      const authToken = token || localStorage.getItem("authToken");

      const data = await fetchWrapper("https://pro.mokshiq.in/save_bbps_transaction", {
        method: "POST",
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : "",
        },
        body: formData,
      });

      return data;
    } catch (err) {
      console.error("Failed to persist transaction to backend:", err);
      return { status: false, message: "Server connection failed while saving transaction." };
    }
  };
  const handleLaunch = () => {
    if (launched) return;
    setLoading(true);
    setSdkError(null);

    // Small timeout so React renders the mount div before SDK tries to find it
    setTimeout(() => {
      try {
        sdkRef.current = BbpsSDK.init({
          selector: "#bbps-widget-mount", // pass the DOM element directly — safer than selector string
          token: "bbsWZqLoQJc2MLt6sXvsKVcb7uEUzdS1M3NPG8DTq2XP+8z8MO31TRMWpElC3Eq6ODkyFTdNQQaXpXgwa0d75OYoxHfxL3M7xwOhpOdkkoqAF9r5l7KBziXKSJghqJw58drFT+9zTSRgDk+PKr5f3hpiiyYYf9lyy8zfgLgw+C5DzJNGEknifJ6tYm+qlPE6mMR37zpfCAiSTF48Rh8mhzH3lpKSkq9djEGSpS9vBxnB75tJLbuMwWZB/cKi0pE2XgfxPkJ3wiUBJX8YxEcjx90gnbYiDulexNxGgb/gkvOh5u0h6P+Z4YYqac+Mf22Hiz4Sh1rG4DDoexCb1GHhgQ==",
          baseUrl: "https://pro.mokshiq.in/init_bbps/",
          partnerId: "BYWP6Q",
          vid: "SAGENT9WW8JB",
          secretKey: "SNZO3TKZCZ30LSMSWDBF9BBV8U2KRS3U",
          theme: "light",
          merchantName: "Softmint Retailer",
          userId: userid,
          onSuccess: async (receipt) => {
            setLoading(true);
            const apiRes = await saveTransactionToBackend(receipt, "SUCCESS");
            setLoading(false);

            if (apiRes?.status) {
              showToast(
                `Payment Processed! Ref: ${apiRes.reference_num || receipt?.rrn || "N/A"}`,
                "success"
              );
            } else {
              showToast(apiRes?.message || "Failed to update ledger balance.", "danger");
            }
          },

          onError: async (errReceipt) => {
            setLoading(true);
            const statusOverride = errReceipt?.status === "PENDING" ? "PENDING" : "FAILED";
            const apiRes = await saveTransactionToBackend(errReceipt, statusOverride);
            setLoading(false);

            showToast(
              apiRes?.message || errReceipt?.message || "Payment process incomplete.",
              statusOverride === "PENDING" ? "warning" : "danger"
            );
          },
        });

        setLaunched(true);
      } catch (err) {
        console.error("BbpsSDK.init failed:", err);
        setSdkError(err?.message || "Could not connect to BBPS gateway.");
      } finally {
        setLoading(false);
      }
    }, 50);
  };

  const handleClose = () => {
    sdkRef.current?.destroy?.();
    sdkRef.current = null;
    setLaunched(false);
    setSdkError(null);
  };

  return (
    <main>
      <div className="container-fluid">
        <div id="bbps-widget-mount"></div>
        {/* Page header */}


        {/* ── Landing card — shown before launch ── */}
        {!launched && (
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="card border-0 shadow-sm text-center" style={{ borderRadius: 20, overflow: "hidden" }}>

                {/* Gradient header */}
                <div style={{ background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #06B6D4 100%)", padding: "40px 32px 32px" }}>
                  <div
                    className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                    style={{ width: 72, height: 72, borderRadius: 20, background: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)" }}
                  >
                    <i className="ph-duotone ph-receipt fs-2 text-white" />
                  </div>
                  <h4 className="text-white fw-800 mb-1">Pay Your Bills</h4>
                  <p className="mb-0" style={{ color: "rgba(255,255,255,0.75)", fontSize: 14 }}>
                    Electricity · Water · Gas · DTH · Broadband &amp; more
                  </p>
                </div>

                {/* Card body */}
                <div className="card-body px-4 py-4">

                  {/* Trust badges */}
                  <div className="d-flex justify-content-center gap-3 mb-4 flex-wrap">
                    {[
                      { icon: "ph-shield-check", label: "100% Secure", color: "#10B981" },
                      { icon: "ph-lightning", label: "Instant Pay", color: "#F59E0B" },
                      { icon: "ph-seal-check", label: "BBPS Certified", color: "#6366F1" },
                    ].map(({ icon, label, color }) => (
                      <div key={label} className="d-flex align-items-center gap-1" style={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}>
                        <i className={`ph-duotone ${icon}`} style={{ fontSize: 16, color }} />
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* Error banner */}
                  {sdkError && (
                    <div className="alert alert-danger d-flex align-items-center gap-2 mb-3 text-start" style={{ borderRadius: 12, fontSize: 13 }}>
                      <i className="ph-duotone ph-warning-circle fs-5 flex-shrink-0" />
                      <span>{sdkError}</span>
                    </div>
                  )}

                  {/* CTA */}
                  <button
                    className="btn btn-primary w-100 fw-700 d-flex align-items-center justify-content-center gap-2"
                    style={{
                      borderRadius: 12, padding: "14px 24px", fontSize: 16,
                      background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                      border: "none", boxShadow: "0 4px 15px rgba(99,102,241,0.35)",
                    }}
                    onClick={handleLaunch}
                    disabled={loading}
                  >
                    {loading ? (
                      <><span className="spinner-border spinner-border-sm" role="status" /> Connecting to BBPS…</>
                    ) : (
                      <><i className="ph-bold ph-currency-inr" /> Pay Your Bills</>
                    )}
                  </button>

                  <p className="text-muted mt-3 mb-0" style={{ fontSize: 11 }}>
                    Secured by Softmint · Powered by BBPS Network
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── SDK widget pane — shown after launch ── */}


      </div>
    </main>
  );
}

function showToast(message, type = "success") {
  const el = document.createElement("div");
  el.className = `alert alert-${type} alert-dismissible position-fixed top-0 end-0 m-3 shadow-lg`;
  el.style.cssText = "z-index:9999;min-width:280px;border-radius:12px";
  el.setAttribute("role", "alert");
  el.innerHTML = `
            <div class="d-flex align-items-center gap-2">
              <i class="ph-duotone ${type === "success" ? "ph-check-circle" : "ph-warning-circle"} fs-5"></i>
              <span class="flex-grow-1">${message}</span>
              <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
            </div>`;
  document.body.appendChild(el);
  setTimeout(() => el?.remove(), 3500);
}